import { Table, Badge, Spinner, Button } from 'react-bootstrap';
import type { Review } from '../types';

interface ReviewListProps {
  reviews: Review[];
  onEditReview: (review: Review) => void;
  onDeleteReview: (review: Review) => void;
  isLoading: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

export const ReviewList = ({
  reviews,
  onEditReview,
  onDeleteReview,
  isLoading,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: ReviewListProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'published':
        return 'success';
      case 'curated':
        return 'info';
      case 'pending':
        return 'warning';
      case 'submitted':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Product ID</th>
            <th>Review ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Rating</th>
            <th>Title</th>
            <th>Status</th>
            <th>Modified At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {reviews.length === 0 ? (
            <tr>
              <td colSpan={9} className="text-center py-4">
                No reviews found
              </td>
            </tr>
          ) : (
            reviews.map((review) => (
              <tr key={review.reviewId}>
                <td>{review.productId}</td>
                <td>{review.reviewId}</td>
                <td>{review.name}</td>
                <td>{review.email}</td>
                <td>
                  <div className="d-flex align-items-center">
                    <span className="me-1">{review.rating}</span>
                    <span style={{ color: '#FFD700' }}>★</span>
                  </div>
                </td>
                <td>{review.title}</td>
                <td>
                  <Badge bg={getStatusVariant(review.status)}>{review.status}</Badge>
                </td>
                <td>{formatDate(review.modifiedAt)}</td>
                <td>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => onEditReview(review)}>
                      Edit
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => onDeleteReview(review)}>
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Load More Button */}
      {hasMore && onLoadMore && (
        <div className="d-flex justify-content-center mt-3">
          <Button variant="outline-primary" onClick={onLoadMore} disabled={isLoadingMore} className="px-4">
            {isLoadingMore ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Loading...
              </>
            ) : (
              'Load More Reviews'
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
